import { useState } from 'react';
import styled from 'styled-components';
import { Ace } from 'ace-builds';
import AceEditor, { IAceEditorProps } from 'react-ace';
import ace from 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/mode-graphqlschema';
import { IconButton, Tooltip } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CodeIcon from '@mui/icons-material/Code';
import { ReactComponent as CopyIcon } from '../../assets/copy.svg';
import { ReactComponent as VIcon } from '../../assets/v.svg';
import { Flex } from '../Flex';
import SvgIcon from '../SvgIcon';
import { WarehouseType } from '../../types';
import { radius, fontFamily } from '../../style/utils';
import { formatSqlCode } from './utils';

// Configure ace editor base path for proper worker loading
// This prevents "Failed to load worker" errors in development
ace.config.set(
  'basePath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);
ace.config.set(
  'modePath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);
ace.config.set(
  'themePath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);
ace.config.set(
  'workerPath',
  'https://cdn.jsdelivr.net/npm/ace-builds@1.32.0/src-noconflict/',
);

const Buttons = styled(Flex)`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${({ theme }) => theme.palette.surface.translucent.scrim};
  border-radius: ${radius('lg')};
`;

const formatCode = (
  code: string,
  mode?: string,
  warehouseType?: WarehouseType,
): string => (mode === 'sql' ? formatSqlCode(code, warehouseType) : code);

type Props = Omit<IAceEditorProps, 'theme'> & {
  mode?: string;
  height?: string;
  fontSize?: number;
  grayedOut?: boolean;
  hideLineNumbers?: boolean;
  warehouseType?: WarehouseType;
};

const CodeEditor = styled(AceEditor).attrs(
  ({
    theme,
    fontSize = 13,
    width = '100%',
    showPrintMargin = false,
    grayedOut,
    hideLineNumbers,
    mode = 'text',
    ...props
  }: Props & { theme: any }) => ({
    // Workaround to the fact that the AceEditor accepts a theme prop itself
    theme: grayedOut ? undefined : 'monokai',
    colorTheme: theme,
    fontSize,
    width,
    showPrintMargin,
    grayedOut,
    onLoad: (editor: Ace.Editor) => {
      editor.renderer.setScrollMargin(10, 10, 0, 0);
      if (hideLineNumbers) {
        editor.renderer.setShowGutter(false);
        editor.renderer.setPadding(10);
      }
    },
    mode,
    ...props,
  }),
)`
  border-radius: ${radius('lg')};

  * {
    font-family: ${fontFamily('code')};
  }

  .error-marker {
    position: absolute;
    border-bottom: 1px solid
      ${({ colorTheme }) => colorTheme.palette.status.error.main};
    border-radius: 0;
    min-width: 3px;
    background-color: ${({ colorTheme }) =>
      colorTheme.palette.status.error.main};
    opacity: 0.3;
    width: 100% !important;
    left: 0 !important;
  }

  .ace_error {
    background-image: none;
  }

  .ace_gutter-cell.ace_error {
    background-color: ${({ colorTheme }) =>
      colorTheme.palette.status.error.main};
    color: ${({ colorTheme }) => colorTheme.palette.content.inverse};
  }

  ${({ readOnly }) =>
    readOnly &&
    `
      .ace_cursor {
          opacity: 0
      }
      .ace_active-line {
        background: none !important;
      }
      .ace_gutter-active-line {
        background: none !important;
      }
`};

  ${({ grayedOut, colorTheme }) =>
    grayedOut &&
    `
      &.ace-tm {
        opacity: 0.5;
        filter: grayscale(100%) brightness(85%);
      }

      .ace_content {
        background-color: ${colorTheme.palette.code.editorBg} !important; 
      }

      .ace_gutter {
        background: ${colorTheme.palette.code.gutterBg} !important; 
        color: ${colorTheme.palette.code.gutterText} !important; 
      }

      .ace_cursor {
        display: none !important; 
      }

      .ace_marker-layer .ace_selection {
        background: ${colorTheme.palette.code.selection} !important; 
      }
`};
`;

const CopyTooltip = ({ isCopied }: { isCopied: boolean }) => (
  <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
    {isCopied && (
      <SvgIcon strokeColor="icon.inverse">
        <VIcon />
      </SvgIcon>
    )}
    {isCopied ? 'Copied!' : 'Copy'}
  </Flex>
);

const CodeEditorWrapper = (props: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const trimmedValue = props.value?.trim() || '';
  const [isFormatted, setIsFormatted] = useState(true);

  // When height is provided, exclude maxLines and minLines to avoid type conflicts
  const { height, maxLines, minLines, warehouseType, ...editorProps } = props;

  const displayValue =
    isFormatted && props.readOnly
      ? formatCode(trimmedValue, props.mode, warehouseType)
      : props.readOnly
      ? trimmedValue
      : props.value || '';

  const handleCopy = () => {
    navigator.clipboard.writeText(displayValue);
    setIsCopied(true);
  };

  const showFormatButton = props.readOnly && props.mode === 'sql';

  const sizeProps = height
    ? { height }
    : {
        maxLines: maxLines || Infinity,
        minLines: minLines || 1,
      };

  return (
    <Flex
      sx={{
        position: 'relative',
        height: props.height,
        width: props.width || '100%',
      }}
    >
      <CodeEditor {...editorProps} {...sizeProps} value={displayValue} />

      <Buttons>
        {showFormatButton && (
          <Tooltip title={isFormatted ? 'Raw' : 'Beautify'}>
            <IconButton onClick={() => setIsFormatted(!isFormatted)}>
              {isFormatted ? (
                <CodeIcon
                  sx={{
                    fontSize: (theme) => theme.tokens.fontSize.lg,
                    color: 'content.inverse',
                  }}
                />
              ) : (
                <AutoFixHighIcon
                  sx={{
                    fontSize: (theme) => theme.tokens.fontSize.lg,
                    color: 'content.inverse',
                  }}
                />
              )}
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={<CopyTooltip isCopied={isCopied} />}>
          <IconButton
            onClick={handleCopy}
            onMouseLeave={() => setIsCopied(false)}
          >
            <SvgIcon width={16} strokeColor="icon.inverse">
              <CopyIcon />
            </SvgIcon>
          </IconButton>
        </Tooltip>
      </Buttons>
    </Flex>
  );
};

export { CodeEditor as BaseCodeEditor };

export default CodeEditorWrapper;
